/**
 * Smart quotes per book language (decision 33): the straight quotes a writer types become the language's
 * typographic quotes and apostrophe in every rendered output (the preview, PDF and EPUB), while the stored source
 * keeps what was typed (decision 66). Table Q in the part 13 log.
 *
 * A typed `"` takes the language's double-quote pair and a typed `'` its single-quote pair, so the writer's choice
 * between the two survives. Forms follow CLDR's quotation delimiters, except where CLDR gives one pair for both
 * (French: the inner pair is “ ”; Dutch: the outer pair is “ ”), so `"` and `'` never collapse into the same marks.
 */
import { at } from '../arrays.ts';
import { decode } from '../font/text.ts';
import type { BookLanguage } from '../theme/metadata.ts';
import { frozen } from '../theme/record.ts';

/** A language's quotation marks: the pair a typed `"` becomes, the pair a quoting `'` becomes. */
export interface QuoteForms {
  readonly double: readonly [open: string, close: string];
  readonly single: readonly [open: string, close: string];
  /** French: a narrow no-break space (U+202F) inside guillemets (Q9, Q10). */
  readonly spaced: boolean;
}

/** The apostrophe every book language uses (U+2019): a typed `'` that isn't a quote becomes this (Q4, Q5, Q7). */
export const APOSTROPHE = '’';

/** The narrow no-break space French sets inside guillemets. */
export const NARROW_NO_BREAK_SPACE = '\u202f';

const forms = (double: string, single: string, spaced = false): QuoteForms => ({
  double: [double.charAt(0), double.charAt(1)],
  single: [single.charAt(0), single.charAt(1)],
  spaced,
});

/** Q11: every book language's quotation marks. */
export const QUOTES: Readonly<Record<BookLanguage, QuoteForms>> = frozen({
  'en-US': forms('“”', '‘’'),
  'en-GB': forms('“”', '‘’'),
  de: forms('„“', '‚‘'),
  fr: forms('«»', '“”', true),
  es: forms('“”', '‘’'),
  it: forms('«»', '“”'),
  pt: forms('“”', '‘’'),
  nl: forms('“”', '‘’'),
  sv: forms('””', '’’'),
  nb: forms('«»', '‘’'),
  nn: forms('«»', '‘’'),
  da: forms('“”', '‘’'),
  fi: forms('””', '’’'),
  pl: forms('„”', '«»'),
  cs: forms('„“', '‚‘'),
  ca: forms('«»', '“”'),
});

/**
 * Text after {@link smartQuotes}, with where each of its UTF-16 units came from: `offsets[i]` is the offset in the
 * original text of unit `i`, and `offsets[text.length]` the original's length (Q12).
 */
export interface Quoted {
  readonly text: string;
  readonly offsets: Uint32Array<ArrayBuffer>;
}

// Each test below reads one whole character (or '' past an edge), so none needs anchors.
const SPACE = /\s/u;
const OPENING = /[\p{Ps}\p{Pd}]/u;
const INITIAL = /\p{Pi}/u;
const WORD = /[\p{L}\p{M}\p{N}]/u;
const DIGIT = /\p{N}/u;
const HARD_BREAK = '\n';
const NO_BREAK_SPACES = new Set(['\u00a0', NARROW_NO_BREAK_SPACE]);

/**
 * `text` (a paragraph's visible text, a hard line break written as a line feed) with its straight quotes made the
 * language's (table Q). Every other character stays as it is, typographic quotes the writer typed included.
 */
export function smartQuotes(text: string, language: BookLanguage): Quoted {
  const { double, single, spaced } = QUOTES[language];
  const closers = new Set([double[1], single[1], APOSTROPHE]);
  const { codePoints, offsets: starts } = decode(text);
  const characters = codePoints.map((codePoint) => String.fromCodePoint(codePoint));
  // The characters made, each with the offset it came from.
  const out: string[] = [];
  const from: number[] = [];
  let doubleOpen = false;
  let singleDepth = 0;
  // Q9: the space after an opening guillemet becomes a narrow no-break space.
  let narrow = false;
  const last = (): string => out.at(-1) ?? '';
  const emit = (character: string, offset: number): void => {
    out.push(character);
    from.push(offset);
  };
  // Q1, Q6, Q13: a quote can open after the paragraph's start, a blank (a hard break's line feed is one), an opening
  // bracket or dash, or an opening quote.
  const canOpen = (): boolean => {
    const previous = last();
    return previous === '' || SPACE.test(previous) || OPENING.test(previous) || (INITIAL.test(previous) && !closers.has(previous));
  };
  characters.forEach((character, i) => {
    const offset = at(starts, i);
    const next = characters[i + 1] ?? '';
    const clearAfter = next !== '' && !SPACE.test(next);
    let made = character;
    if (narrow) {
      made = NARROW_NO_BREAK_SPACE;
      narrow = false;
    } else if (character === '"') {
      // Q1–Q3: open where the quote can only open, close where it can only close, and by what is open otherwise.
      const open = canOpen() && (clearAfter || !doubleOpen);
      doubleOpen = open;
      made = open ? double[0] : double[1];
    } else if (character === "'") {
      // Q4: inside a word (don't, l'homme, 5'10); Q5: a shortened year ('90s).
      const elided = (WORD.test(characters[i - 1] ?? '') && WORD.test(next)) || (canOpen() && DIGIT.test(next));
      if (!elided && canOpen() && clearAfter) {
        // Q6
        singleDepth += 1;
        made = single[0];
      } else if (!elided && singleDepth > 0) {
        // Q7: closes the innermost open single quote.
        singleDepth -= 1;
        made = single[1];
      } else {
        // Q4, Q5, and Q7 with none open: a plural possessive or an elision (the boys', o').
        made = APOSTROPHE;
      }
    }
    // Q10: a closing guillemet in French sits after a narrow no-break space.
    if (spaced && made === '»') {
      const previous = last();
      if (previous === ' ') out[out.length - 1] = NARROW_NO_BREAK_SPACE;
      else if (previous !== '' && previous !== HARD_BREAK && !NO_BREAK_SPACES.has(previous)) emit(NARROW_NO_BREAK_SPACE, offset);
    }
    emit(made, offset);
    // Q9: an opening guillemet in French sits before a narrow no-break space.
    if (spaced && made === '«') {
      if (next === ' ') narrow = true;
      else if (next !== '' && next !== HARD_BREAK && !NO_BREAK_SPACES.has(next)) emit(NARROW_NO_BREAK_SPACE, offset);
    }
  });
  // Q12: one offset per UTF-16 unit (a character outside the BMP has two, its own and the next).
  const offsets: number[] = [];
  out.forEach((character, k) => {
    for (let unit = 0; unit < character.length; unit += 1) offsets.push(at(from, k) + unit);
  });
  offsets.push(text.length);
  return { text: out.join(''), offsets: Uint32Array.from(offsets) };
}
